import React, { useEffect, useState } from 'react';
import type {
  JiraConnectionAck,
  JiraConnectionSettings,
  UserSettingsPublic,
  UserSettingsUpdate,
} from '../../services/clients/types';
import { hintStyle, sectionStyle } from './settingsStyles';
import { loadJiraConnectionAck, runJiraConnectionTest } from './jiraSettingsActions';
import {
  initialJiraOauthReturnHint,
  jiraApiTokenConnected,
  jiraOauthReturnHintColor,
  resolveJiraConnectionChrome,
  stripJiraReturnQueryFromUrl,
} from './jiraSectionHelpers';
import { SettingsConnectionStatus } from './SettingsConnectionStatus';
import { SettingsJiraAdvancedForm } from './SettingsJiraAdvancedForm';
import { ProviderIcon } from './ProviderIcon';
import { ConnectionTestLabel } from './ConnectionTestLabel';
import { Button } from '../../ui/common/Button';

export interface SettingsJiraSectionProps {
  inAccordion?: boolean;
  settings: UserSettingsPublic;
  saving: boolean;
  jiraDraft: JiraConnectionSettings;
  jiraTokenDraft: string;
  onJiraDraft: (value: JiraConnectionSettings) => void;
  onJiraTokenDraft: (value: string) => void;
  onAutoSaveJiraConnection: (value: JiraConnectionSettings) => void;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
}

export const SettingsJiraSection: React.FC<SettingsJiraSectionProps> = ({
  settings,
  saving,
  jiraDraft,
  jiraTokenDraft,
  onJiraDraft,
  onJiraTokenDraft,
  onAutoSaveJiraConnection,
  onSavePatch,
  inAccordion = false,
}) => {
  const advancedOpen = true;
  const [ack, setAck] = useState<JiraConnectionAck | null>(null);
  const [ackBusy, setAckBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [oauthReturnHint] = useState<string | null>(initialJiraOauthReturnHint);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = stripJiraReturnQueryFromUrl(window.location.href);
    if (next) window.history.replaceState({}, '', next);
  }, []);

  const tokenStatus = settings.secrets.jiraApiToken;
  const apiTokenConnected = jiraApiTokenConnected(Boolean(tokenStatus?.configured), settings.jira);
  const chrome = resolveJiraConnectionChrome({
    ack,
    oauthConnected: settings.jira.oauthConnected,
    oauthSiteName: settings.jira.oauthSiteName,
    apiTokenConnected,
    lastFour: tokenStatus?.lastFour ?? null,
  });

  useEffect(() => {
    let mounted = true;
    void loadJiraConnectionAck((newAck) => {
      if (mounted) {
        setAck(newAck);
      }
    });
    return () => {
      mounted = false;
    };
  }, [settings.jira.authMethod, settings.jira.oauthConnected, tokenStatus?.configured]);

  return (
    <section style={{ ...sectionStyle, borderBottom: 'none' }} data-testid="settings-jira-section">
      <SettingsConnectionStatus
        testIdPrefix="settings-jira"
        title="Jira"
        chipLabel={chrome.chipLabel}
        chipOk={chrome.chipOk}
        hint="Conecta Atlassian Cloud con site URL, email y API token personal. El estado distingue credenciales faltantes y una identidad verificada."
        icon={<ProviderIcon name="jira" />}
        headerAction={
          !inAccordion ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-jira-test-connection"
              disabled={ackBusy || !(apiTokenConnected || settings.jira.oauthConnected)}
              loading={ackBusy}
              onClick={() =>
                void runJiraConnectionTest({
                  setBusy: setAckBusy,
                  setError: setLocalError,
                  setAck,
                })
              }
            >
              <ConnectionTestLabel />
            </Button>
          ) : undefined
        }
        compact={inAccordion}
      >
        {oauthReturnHint ? (
          <p
            data-testid="settings-jira-oauth-return"
            style={{ ...hintStyle, color: jiraOauthReturnHintColor(oauthReturnHint) }}
          >
            {oauthReturnHint}
          </p>
        ) : null}

        {localError ? (
          <p
            data-testid="settings-jira-local-error"
            style={{ ...hintStyle, color: 'var(--z-danger)' }}
          >
            {localError}
          </p>
        ) : null}

        <div
          data-testid="settings-jira-manual-setup"
          style={{
            color: 'var(--z-text-2)',
            fontSize: 13,
          }}
        >
          Conexión con site URL, email y API token personal
        </div>

        <SettingsJiraAdvancedForm
          open={advancedOpen}
          saving={saving}
          jiraDraft={jiraDraft}
          jiraTokenDraft={jiraTokenDraft}
          tokenConfigured={Boolean(tokenStatus?.configured)}
          onJiraDraft={onJiraDraft}
          onJiraTokenDraft={onJiraTokenDraft}
          onAutoSaveJiraConnection={onAutoSaveJiraConnection}
          onSavePatch={onSavePatch}
        />
      </SettingsConnectionStatus>
    </section>
  );
};
